import { execFile, spawn } from 'node:child_process'
import fs from 'node:fs/promises'
import os from 'node:os'
import path from 'node:path'
import { Readable } from 'node:stream'
import type { ReadableStream } from 'node:stream/web'
import zlib from 'node:zlib'
import tar from 'tar-fs'

import type { ScalarConfig } from '@scalar-org/scalar-config'
import type { IsolateOptions } from '@scalar-org/ssg-docs/isolate'

import { config as cliConfig } from '@/config'
import { getAuthData } from '@/helpers'
import { runCommand } from '@/helpers/run-command'
import { output } from '@/helpers/std-out'

// ~/.scalar
const ISOLATE_EXTRACTION_DIR = path.join(os.homedir(), '.scalar')
// ~/.scalar/isolate
const ISOLATE_DIR = path.join(ISOLATE_EXTRACTION_DIR, 'isolate')

// Get the existing installed isolate version
async function getExistingIsolateVersion() {
  try {
    const packageJsonString = await fs.readFile(
      path.join(ISOLATE_DIR, 'package.json'),
      'utf8',
    )

    const packageJson = JSON.parse(packageJsonString)

    return packageJson.version as string
  } catch {
    return undefined
  }
}

async function downloadWindowsIsolate(isolateUrl: string) {
  return new Promise<string>((resolve, reject) => {
    const psScript = `
$ErrorActionPreference = "Stop"

$targetDir = Join-Path $env:USERPROFILE ".scalar"
$tempFile = Join-Path $env:TEMP "scalar.tar.gz"

# Ensure target directory exists
New-Item -ItemType Directory -Force -Path $targetDir | Out-Null

# Download tarball
$ProgressPreference = 'SilentlyContinue'
Invoke-WebRequest -Uri "${isolateUrl}" -OutFile $tempFile

# Extract (uses built-in tar on Windows)
tar -xzf $tempFile -C $targetDir

# Cleanup
Remove-Item $tempFile
`

    execFile(
      'powershell.exe',
      [
        '-NoProfile',
        '-NonInteractive',
        '-ExecutionPolicy',
        'Bypass',
        '-Command',
        psScript,
      ],
      { windowsHide: true },
      (error, stdout, stderr) => {
        if (error) {
          error.stderr = stderr
          return reject(error)
        }

        resolve(stdout)
      },
    )
  })
}

// Download the specified isolateVersion from the CDN and extract it.
async function downloadIsolate() {
  const existingIsolateVersion = await getExistingIsolateVersion()

  if (cliConfig.ssgDocsIsolateVersion === existingIsolateVersion) return

  await fs.rm(ISOLATE_EXTRACTION_DIR, { force: true, recursive: true })

  const logger = output
    .info({ animate: true })
    .loader('Downloading docs isolate. This may take a minute...')

  const ISOLATE_URL = `${cliConfig.cdnUrl}/isolates/${cliConfig.ssgDocsIsolateVersion}-isolate.tar.gz`

  if (process.platform === 'win32') {
    await downloadWindowsIsolate(ISOLATE_URL)
  } else {
    const response = await fetch(ISOLATE_URL)

    await new Promise((resolve) => {
      Readable.fromWeb(response.body as ReadableStream)
        .pipe(zlib.createGunzip())
        .pipe(tar.extract(ISOLATE_EXTRACTION_DIR))
        .on('finish', resolve)
    })

    // On MacOS, since the isolate contains native binaries for things like vite,
    // we need to remove the quarantine flags from the downloaded isolate
    // before it can be ran.
    if (process.platform === 'darwin')
      await runCommand(`xattr -dr com.apple.quarantine ${ISOLATE_DIR}`)
  }

  logger.update('loader', 0, { content: 'Download complete.' })
  logger.finish()
}

// Spawn the isolate process passing the IsolateOptions
async function runIsolate(isolateOptions: IsolateOptions) {
  const child = spawn('node', ['dist/isolate-entry.js'], { cwd: ISOLATE_DIR })

  child.stdin.write(JSON.stringify(isolateOptions))
  child.stdin.end()

  child.stdout.pipe(process.stdout)
  child.stderr.pipe(process.stderr)

  child.on('close', (code) => {
    console.log(`Process exited with code ${code}.`)
  })
}

async function getAccessToken() {
  try {
    const { accessToken } = await getAuthData({ noExit: true })

    return accessToken
  } catch {
    output
      .info()
      .message(
        "You're not signed in. Private Scalar content will not be fetched.",
      )
      .print()
    return ''
  }
}

// Preview a scalar docs V2 project
export async function previewV2Project({
  logLevel,
  configPath,
  port,
  config,
  host,
}: {
  logLevel: 'debug' | 'info'
  configPath: string
  port: number
  config: ScalarConfig
  host?: string | boolean
}) {
  // Resolve the root of the project
  const root = path.resolve(path.dirname(configPath), config.root ?? './')

  // The config path relative to the root of the uploaded project
  const relativeConfigPath = path.relative(root, configPath)

  const accessToken = await getAccessToken()

  await downloadIsolate()

  output.info().message('Starting preview...').print()

  await runIsolate({
    configPath: relativeConfigPath,
    projectDir: root,
    logLevel,
    domain: 'localhost',
    mode: 'preview',
    previewServerPort: port,
    registryBaseUrl: cliConfig.projects.registry,
    baseUrl: cliConfig.services.api,
    token: accessToken,
    host,
  })
}
